import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { ArrowRight, CheckCircle2, Check } from "lucide-react";
import { trackEvent } from "../lib/api";

// Assessment-type-aware copy. Greenfield/Brownfield keep the original content;
// Cloud Migration renders migration-specific wording. Layout/styling/icons/
// buttons/routing are shared and unchanged.
const CONTENT = {
  default: {
    title: "Evaluation submitted",
    lead: "Our SAP experts are reviewing your warehouse setup and will share tailored recommendations shortly.",
    steps: [
      "Review of your warehouse landscape and processes",
      "Identification of improvement opportunities and SAP fit",
      "Recommended approach, timeline, and next steps",
    ],
    timeline: "You can expect a response within 1–2 business days.",
  },
  cloud_migration: {
    title: "Cloud Migration Assessment Submitted",
    lead: "Thank you for completing your Cloud Migration assessment. Our SAP warehouse experts will review your current landscape, migration complexity, and transformation objectives before preparing your migration roadmap.",
    steps: [
      "Review of your current SAP warehouse landscape and technical architecture",
      "Assessment of migration complexity, risks, and project dependencies",
      "Recommended migration approach, implementation roadmap, timeline, and next steps",
    ],
    timeline: "Your Cloud Migration assessment will be reviewed by our SAP migration specialists. We will contact you with the recommended migration strategy and next steps.",
  },
};

/**
 * SmartEvaluate — Final Submission Confirmation
 *
 * Minimal, premium completion screen. No progress bar, no step numbers,
 * no deliverables checklist — just the completion message, a timestamp,
 * and a secondary action to start another evaluation.
 */
export default function SubmissionConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submittedAt] = useState(() => new Date());
  // Capture the submitted scenario ONCE (nav state, then localStorage fallback
  // for page refresh) before the cleanup effect clears draft state.
  const [scenario] = useState(
    () => location.state?.scenario || localStorage.getItem("warehousex_submitted_scenario") || ""
  );
  const content = CONTENT[scenario] || CONTENT.default;

  // Clear ALL evaluation-draft state the moment we land on the confirmation
  // screen so that:
  //   • "Start New Evaluation" cannot inherit previous answers.
  //   • Browser Back cannot re-open the submitted flow in editable form.
  //   • A stale assessment_id can never be PUT against by Module 2/3 useEffects.
  // The Header branding cache (primes4.brand.logo.v1) is intentionally left
  // alone — that is branding, not evaluation data.
  useEffect(() => {
    try {
      const email = localStorage.getItem("warehousex_email");
      trackEvent("assessment_submitted_viewed", email);
    } catch { /* ignore */ }
    try {
      localStorage.removeItem("warehousex_assessment_id");
      localStorage.removeItem("warehousex_email");
      localStorage.removeItem("warehousex_submitted_scenario");
    } catch { /* ignore */ }
  }, []);

  const handleStartNew = () => {
    // Defensive: re-clear in case another tab wrote back between mount and click.
    try {
      localStorage.removeItem("warehousex_assessment_id");
      localStorage.removeItem("warehousex_email");
    } catch { /* ignore */ }
    // Jump straight to Step 1 with a replaced history entry so browser Back
    // from Step 1 skips past this confirmation screen (and the submitted
    // module pages behind it).
    navigate("/evaluate/module-1", { replace: true });
  };

  const handleBackHome = () => {
    try {
      localStorage.removeItem("warehousex_assessment_id");
      localStorage.removeItem("warehousex_email");
    } catch { /* ignore */ }
    navigate("/", { replace: true });
  };

  const submittedLabel = submittedAt.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="submission-confirmation-page">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14">
        <div className="w-full max-w-xl mx-auto text-center">
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8 sm:p-12">
            {/* Success badge */}
            <div className="flex justify-center mb-6">
              <div
                className="w-14 h-14 rounded-full bg-[#E0F2FE] border border-[#025979]/20 flex items-center justify-center"
                data-testid="submission-badge"
              >
                <CheckCircle2 className="w-7 h-7 text-[#025979]" strokeWidth={1.75} />
              </div>
            </div>

            {/* Title */}
            <h1
              data-testid="submission-title"
              className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1F2937]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {content.title}
            </h1>

            {/* Completion message */}
            <p
              data-testid="submission-lead"
              className="mt-4 text-base text-[#4B5563] leading-relaxed max-w-md mx-auto"
            >
              {content.lead}
            </p>

            {/* What happens next — consulting engagement cues */}
            <div
              data-testid="submission-next-steps"
              className="mt-8 text-left max-w-md mx-auto bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-5 py-4"
            >
              <p
                className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                What happens next
              </p>
              <ul className="mt-3 space-y-2.5 text-sm text-[#374151] leading-relaxed">
                {content.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#025979] mt-0.5 shrink-0" strokeWidth={2.25} />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Response timeline */}
            <p
              data-testid="submission-timeline"
              className="mt-5 text-xs text-[#6B7280]"
            >
              {content.timeline}
            </p>

            {/* Primary CTA + secondary link */}
            <div className="mt-7 flex flex-col items-center gap-3">
              <Button
                data-testid="start-new-assessment-btn"
                onClick={handleStartNew}
                className="bg-[#025979] hover:bg-[#03738C] text-white h-11 px-8 text-sm font-semibold rounded-sm transition-colors shadow-sm"
              >
                Evaluate another warehouse
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <button
                data-testid="back-to-smartstrategy-link"
                onClick={handleBackHome}
                className="text-xs text-[#025979] hover:text-[#03738C] underline-offset-4 hover:underline transition-colors"
              >
                Back to SmartStrategy Home
              </button>
            </div>

            {/* Subtle timestamp */}
            <p
              data-testid="submission-timestamp"
              className="mt-8 text-[11px] text-[#6B7280] uppercase tracking-wider"
            >
              Submitted {submittedLabel}
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
